import React, { useState, useEffect } from "react";
import { addDoc, collection, doc, getDoc, setDoc, updateDoc, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../firebase";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";

const Admin = () => {
    const [statusMessage, setStatusMessage] = useState("");
    const [resetDate, setResetDate] = useState("");
    const [currentResetDate, setCurrentResetDate] = useState("");
    const [isResettingPoints, setIsResettingPoints] = useState(false);

    // Fetch current reset date from Firestore
    useEffect(() => {
        const fetchResetDate = async () => {
            const docRef = doc(firestore, "settings", "bingoReset");
            const docSnap = await getDoc(docRef);
            if (docSnap.exists() && docSnap.data().resetDate) {
                const date = docSnap.data().resetDate.toDate();
                setCurrentResetDate(date.toISOString().slice(0, 10));
            }
        };
        fetchResetDate();
    }, []);

    // Function to create a shared Bingo Grid
    const createSharedBingoGrid = async (title, challenges) => {
        try {
            setStatusMessage("Creating Bingo Grid...");
            // Step 1: Create the BingoGrid
            const gridRef = await addDoc(collection(firestore, "BingoGrid"), {
                Title: title,
                Timestamp: new Date(),
            });

            const gridId = gridRef.id; // Retrieve the Grid_ID

            // Step 2: Add challenges to BingoCell
            const cellPromises = challenges.map((challenge) =>
                addDoc(collection(firestore, "BingoCell"), {
                    Grid_ID: gridId,
                    Challenge: challenge,
                    Used: false, // Default for shared cells
                })
            );

            await Promise.all(cellPromises);
            setStatusMessage("Shared Bingo Grid and Cells created successfully!");
        } catch (error) {
            console.error("Detailed error creating Bingo Grid:", error);
            setStatusMessage(`Error creating Bingo Grid: ${error.message}`);
        }
    };

    // Handle button click to create the Bingo Grid
    const handleCreateBingoGrid = () => {
        const challenges = [
            'A Book about nature',
            'A Book released in the last year',
            'A Book set in your home country',
            'A Horror Book',
            'A Book with a one word title',
            'A Book turned into a movie',
            'A Book with magic',
            'A Book by a Canadian/French author',
            'A Poem',
            'A Mystery Book',
            'A Book about a family',
            'Use a Cookbook',
            'A Book with "Of" in the title',
            'A Book on Myths/Legends', 
            'Tell a friend about BiblioBingo (Any book)',
            'A Book that starts a series',
        ];
        createSharedBingoGrid("July/August Reading Challenge", challenges);
    };

    // Function to update the reset date in Firestore
    const handleSaveResetDate = async () => {
        if (!resetDate) return;
        try {
            const docRef = doc(firestore, "settings", "bingoReset");
            await setDoc(docRef, { resetDate: new Date(resetDate + 'T23:59:59') });
            setStatusMessage("Reset date updated!");
            setCurrentResetDate(resetDate);
            showSuccessToast("Reset date updated successfully!");
        } catch (error) {
            setStatusMessage("Error updating reset date: " + error.message);
            showErrorToast("Error updating reset date: " + error.message);
        }
    };

    // Function to reset all users' points and archive them
    const handleResetPoints = async () => {
        if (!window.confirm('Are you sure you want to reset all users\' points? This will archive current points and reset them to 0.')) {
            return;
        }

        setIsResettingPoints(true);
        setStatusMessage("Resetting points...");

        try {
            // Get all users with points > 0
            const usersQuery = query(collection(firestore, "users"), where("points", ">", 0));
            const usersSnapshot = await getDocs(usersQuery);
            
            if (usersSnapshot.empty) {
                setStatusMessage("No users with points found to reset.");
                showErrorToast("No users with points found to reset.");
                return;
            }

            let processedCount = 0;
            const totalUsers = usersSnapshot.size;
            const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

            // Process each user
            for (const userDoc of usersSnapshot.docs) {
                const userData = userDoc.data();
                const currentPoints = userData.points || 0;
                
                if (currentPoints > 0) {
                    // Archive the points
                    await addDoc(collection(firestore, "pointsArchive"), {
                        userId: userDoc.id,
                        email: userData.email || 'unknown',
                        points: currentPoints,
                        month: currentMonth,
                        archivedAt: new Date(),
                        displayName: userData.name || userData.email || 'Unknown User'
                    });

                    // Reset points to 0
                    await updateDoc(userDoc.ref, { points: 0 });

                    processedCount++;
                    setStatusMessage(`Processing... ${processedCount}/${totalUsers} users`);
                }
            }

            setStatusMessage(`Successfully reset points for ${processedCount} users!`);
            showSuccessToast(`Successfully reset points for ${processedCount} users!`);
            
        } catch (error) {
            console.error("Error resetting points:", error);
            setStatusMessage(`Error resetting points: ${error.message}`);
            showErrorToast(`Error resetting points: ${error.message}`);
        } finally {
            setIsResettingPoints(false);
        }
    };

    return (
        <div className="admin-container">
            <h1>Admin Page</h1>
            
            <div style={{ marginBottom: '2rem' }}>
                <h2>Bingo Grid Reset Date</h2>
                <p>Current reset date: <b>{currentResetDate || 'Not set'}</b></p>
                <input
                    type="date"
                    value={resetDate}
                    onChange={e => setResetDate(e.target.value)}
                />
                <button onClick={handleSaveResetDate} style={{ marginLeft: '1rem' }}>Save Reset Date</button>
            </div>

            <div style={{ marginBottom: '2rem' }}>
                <h2>Points Reset</h2>
                <p style={{ color: '#666', fontSize: '14px' }}>
                    This will archive all users' current points and reset them to 0. 
                    Archived points will be stored in the pointsArchive collection.
                </p>
                <button 
                    onClick={handleResetPoints} 
                    disabled={isResettingPoints}
                    style={{ 
                        backgroundColor: isResettingPoints ? '#ccc' : '#dc3545',
                        marginTop: '10px'
                    }}
                >
                    {isResettingPoints ? 'Resetting Points...' : 'Reset All Points'}
                </button>
            </div>

            <button onClick={handleCreateBingoGrid}>Create Bingo Grid</button>
            <p>{statusMessage}</p>
        </div>
    );
};

export default Admin;
